import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Select from '../../ui/PortalSelect';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Swal from 'sweetalert2';
import { baseURL } from '../backend_url';
import {
    deriveVendorAccountSelection,
    loadAccessibleCompanies,
    readSessionUser,
} from '../accountAccess';
import './RolesSetup.css';

const DEFAULT_SHOW_FOR = 'Corporate Only';

const COLUMN_LIST = [
    'Access Specifier',
    'Account Access',
    'Rate Code Selection',
    'Copy Rates',
    'Days Applicable Selection',
    'Excel Upload',
    'OTA Selection',
    'Car Class Selection',
    'Type of Adjustment/ Promotion / Blackout',
];

const ACCESS_SPECIFIER_OPTIONS = ['N/A', 'Edit', 'View'];
const ACCOUNT_ACCESS_OPTIONS = [
    'N/A',
    'Only own corporate',
    'Own Corporate + Affiliates',
    'Only own affiliate',
];
const YES_NO_OPTIONS = ['Yes', 'No'];
const TYPE_OPTIONS = ['Date Based Only', 'All'];
const SHOW_FOR_OPTIONS = ['Corporate Only', 'Both Corporate and Affiliate'];
const ACCOUNT_SCOPE_OPTIONS = [
    'Permission based',
    'Own account',
    'Own Corporate + Affiliates',
    'Selected accounts',
    'All accounts',
];

const ROLE_SELECT_PROPS = {
    className: 'roles-setup-select',
    classNamePrefix: 'roles-setup-select',
    menuPosition: 'fixed',
    menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
};

const MODULE_FILTER_OPTIONS = [
    { value: 'all', label: 'All modules' },
    { value: 'standard', label: 'Standard permissions' },
    { value: 'advanced', label: 'Advanced permissions' },
    { value: 'fixed', label: 'Not configurable' },
];

// These modules were intentionally displayed without editable permission cells
// in the original grid. They still receive their existing hidden N/A key so the
// saved role-definition shape remains compatible with the backend.
const NON_CONFIGURABLE_MODULES = new Set([
    'Account',
    'AccountLocations',
    'OptionalServicesImages',
    'OptionalServicesStandard',
    'FleetStandard',
    'Cars',
    'RateID',
    'AccessSetup',
]);

const RATE_CODE_MODULES = new Set([
    'MinimumRate',
    'RateUpdate',
    'RateSnapshot',
    'Adjustment',
    'Promotion',
    'Blackout',
    'CeilingRate',
]);
const DAYS_MODULES = new Set(['RateUpdate', 'Adjustment', 'Promotion', 'Blackout']);
const OTA_MODULES = new Set(['Adjustment', 'Promotion', 'Blackout']);
const CAR_CLASS_MODULES = new Set([
    'Adjustment',
    'Promotion',
    'Blackout',
    'UtilizationBasedAdjustment',
]);
const TYPE_MODULES = new Set(['Adjustment', 'Promotion', 'Blackout']);

const validationSchema = Yup.object().shape({
    roleName: Yup.string().trim().required('Required'),
});

const removeSpaces = (value = '') => String(value).split(' ').join('');

const getGridKey = (moduleName, columnName) => (
    `${removeSpaces(moduleName)}_${columnName}`
);

const getApplicableColumnIndexes = (moduleName) => {
    const normalizedName = removeSpaces(moduleName);

    if (NON_CONFIGURABLE_MODULES.has(normalizedName)) {
        return new Set();
    }

    const applicableColumns = new Set([0, 1]);

    if (RATE_CODE_MODULES.has(normalizedName)) applicableColumns.add(2);
    if (normalizedName === 'RateUpdate') applicableColumns.add(3);
    if (DAYS_MODULES.has(normalizedName)) applicableColumns.add(4);
    if (normalizedName === 'RateUpdate') applicableColumns.add(5);
    if (OTA_MODULES.has(normalizedName)) applicableColumns.add(6);
    if (CAR_CLASS_MODULES.has(normalizedName)) applicableColumns.add(7);
    if (TYPE_MODULES.has(normalizedName)) applicableColumns.add(8);

    return applicableColumns;
};

const getDefaultGridValue = (moduleName, columnIndex) => {
    const normalizedName = removeSpaces(moduleName);
    const applicableColumns = getApplicableColumnIndexes(moduleName);

    // The previous implementation submitted N/A for Access Specifier even for
    // rows whose permission controls were intentionally hidden.
    if (columnIndex === 0) return 'N/A';
    if (!applicableColumns.has(columnIndex)) return '';
    if (columnIndex === 1) return 'Only own corporate';
    if (columnIndex >= 2 && columnIndex <= 7) return 'No';
    if (columnIndex === 8 && TYPE_MODULES.has(normalizedName)) return 'Date Based Only';

    return '';
};

export const buildInitialGrid = (modules) => {
    const initialGrid = {};

    modules.forEach((module) => {
        if (!module || typeof module.name !== 'string' || !module.name.trim()) return;

        COLUMN_LIST.forEach((columnName, columnIndex) => {
            initialGrid[getGridKey(module.name, columnName)] = getDefaultGridValue(
                module.name,
                columnIndex,
            );
        });
    });

    return initialGrid;
};

export const buildRoleIdentity = (companyId, roleName, existingRoleId = '') => {
    const normalizedRoleName = String(roleName ?? '').trim();

    return {
        role_id: existingRoleId || `${companyId}_${normalizedRoleName}`,
        role_name: normalizedRoleName,
    };
};

const getOptionsWithCurrentValue = (options, currentValue) => {
    if (!currentValue || options.includes(currentValue)) return options;
    return [...options, currentValue];
};

const getColumnOptions = (columnIndex) => {
    if (columnIndex === 0) return ACCESS_SPECIFIER_OPTIONS;
    if (columnIndex === 1) return ACCOUNT_ACCESS_OPTIONS;
    if (columnIndex >= 2 && columnIndex <= 7) return YES_NO_OPTIONS;
    return TYPE_OPTIONS;
};

const parseJsonResponse = async (response, errorMessage) => {
    if (!response.ok) {
        throw new Error(`${errorMessage} (${response.status})`);
    }

    return response.json();
};

export default function UpdateForm() {
    const [companyDetail, setCompanyDetail] = useState([]);
    const [companyName, setCompanyName] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [accountSelectorDisabled, setAccountSelectorDisabled] = useState(false);
    const [companiesLoading, setCompaniesLoading] = useState(true);
    const [companiesError, setCompaniesError] = useState('');

    const [createNewFlag, setCreateNewFlag] = useState(false);
    const [roleName, setRoleName] = useState('');
    const [showFor, setShowFor] = useState(DEFAULT_SHOW_FOR);
    const [allModulesAccess, setAllModulesAccess] = useState(false);
    const [accountScope, setAccountScope] = useState('Permission based');

    const [modulesList, setModuleList] = useState([]);
    const [modulesLoading, setModulesLoading] = useState(true);
    const [modulesError, setModulesError] = useState('');
    const [grid, setGrid] = useState({});
    const [moduleSearch, setModuleSearch] = useState('');
    const [moduleFilter, setModuleFilter] = useState('all');

    const [listOfRoles, setListOfRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [rolesError, setRolesError] = useState('');

    const [editFlag, setEditFlag] = useState(false);
    const [editingRoleId, setEditingRoleId] = useState('');
    const [validationMsg, setValidationMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [saving, setSaving] = useState(false);
    const rolesRequestId = useRef(0);

    const formOptions = useMemo(() => ({
        resolver: yupResolver(validationSchema),
    }), []);

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm(formOptions);

    const resetRoleEditor = useCallback((closeEditor = false) => {
        setRoleName('');
        setShowFor(DEFAULT_SHOW_FOR);
        setAllModulesAccess(false);
        setAccountScope('Permission based');
        setGrid(buildInitialGrid(modulesList));
        setValue('roleName', '');
        setEditFlag(false);
        setEditingRoleId('');
        if (closeEditor) setCreateNewFlag(false);
    }, [modulesList, setValue]);

    const loadCompanies = useCallback(async () => {
        setCompaniesLoading(true);
        setCompaniesError('');

        try {
            const { user, error } = readSessionUser();
            if (error) throw new Error(error);

            const accounts = await loadAccessibleCompanies(
                baseURL,
                user,
                'AccessSetup_Account Access',
                { suppressGlobalError: true },
            );
            const selection = deriveVendorAccountSelection(accounts, user);
            const validAccounts = selection.accounts.filter((company) => (
                company
                && company._id !== undefined
                && typeof company.company_name === 'string'
            ));

            setCompanyDetail(validAccounts);
            setAccountSelectorDisabled(selection.isSelectorDisabled);
            if (selection.shouldAutoSelect) {
                setCompanyId(selection.selectedAccountId);
                setCompanyName(selection.selectedAccountName);
            }
        } catch (error) {
            setCompanyDetail([]);
            setAccountSelectorDisabled(false);
            setCompaniesError(error.message || 'Unable to load accounts. Please try again.');
        } finally {
            setCompaniesLoading(false);
        }
    }, []);

    const loadModules = useCallback(async () => {
        setModulesLoading(true);
        setModulesError('');

        try {
            const response = await fetch(`${baseURL}/get_modules`);
            const data = await parseJsonResponse(response, 'Unable to load access modules');

            if (!Array.isArray(data)) {
                throw new Error('The modules response was not in the expected format');
            }

            const validModules = data.filter((module) => (
                module && typeof module.name === 'string' && module.name.trim()
            ));

            setModuleList(validModules);
            setGrid((currentGrid) => ({
                ...buildInitialGrid(validModules),
                ...currentGrid,
            }));
        } catch (error) {
            setModulesError(error.message || 'Unable to load access modules. Please try again.');
        } finally {
            setModulesLoading(false);
        }
    }, []);

    const loadRoles = useCallback(async (accountId) => {
        if (!accountId) return;

        const requestId = rolesRequestId.current + 1;
        rolesRequestId.current = requestId;
        setRolesLoading(true);
        setRolesError('');

        try {
            const response = await fetch(
                `${baseURL}/get_roles_setup_data/${accountId}?companyNumber=${encodeURIComponent(accountId)}`,
            );
            const data = await parseJsonResponse(response, 'Unable to load existing roles');

            if (!Array.isArray(data)) {
                throw new Error('The roles response was not in the expected format');
            }

            if (rolesRequestId.current === requestId) setListOfRoles(data);
        } catch (error) {
            if (rolesRequestId.current === requestId) {
                setListOfRoles([]);
                setRolesError(error.message || 'Unable to load existing roles. Please try again.');
            }
        } finally {
            if (rolesRequestId.current === requestId) setRolesLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCompanies();
        loadModules();
        setValue('roleName', '');
    }, [loadCompanies, loadModules, setValue]);

    useEffect(() => {
        if (companyId) loadRoles(companyId);
    }, [companyId, loadRoles]);

    const companyNameOptions = useMemo(() => companyDetail.map((company) => ({
        value: String(company._id),
        label: `${company.company_name} (${company._id})`,
        companyName: company.company_name,
    })), [companyDetail]);

    const selectedCompany = useMemo(() => (
        companyNameOptions.find((option) => option.value === companyId) || null
    ), [companyId, companyNameOptions]);

    const showForOptions = useMemo(() => (
        getOptionsWithCurrentValue(SHOW_FOR_OPTIONS, showFor)
            .map((value) => ({ value, label: value }))
    ), [showFor]);

    const filteredModules = useMemo(() => {
        const normalizedSearch = moduleSearch.trim().toLowerCase();

        return modulesList.filter((module) => {
            const applicableColumnCount = getApplicableColumnIndexes(module.name).size;
            const matchesSearch = !normalizedSearch
                || module.name.toLowerCase().includes(normalizedSearch);
            const matchesFilter = moduleFilter === 'all'
                || (moduleFilter === 'fixed' && applicableColumnCount === 0)
                || (moduleFilter === 'standard' && applicableColumnCount === 2)
                || (moduleFilter === 'advanced' && applicableColumnCount > 2);

            return matchesSearch && matchesFilter;
        });
    }, [moduleFilter, moduleSearch, modulesList]);

    const handleInputChangeCompanyName = (selectedOption) => {
        rolesRequestId.current += 1;
        setCompanyId(selectedOption?.value || '');
        setCompanyName(selectedOption?.companyName || '');
        setListOfRoles([]);
        setRolesError('');
        setValidationMsg('');
        setSuccessMsg('');
        resetRoleEditor(true);
    };

    const handleInputChangeGrid = (value, moduleName, columnName) => {
        const key = getGridKey(moduleName, columnName);
        setGrid((currentGrid) => ({ ...currentGrid, [key]: value }));
    };

    const openCreateForm = () => {
        resetRoleEditor(false);
        setValidationMsg('');
        setSuccessMsg('');
        setCreateNewFlag(true);
    };

    const submitHandler = async () => {
        setSuccessMsg('');
        setValidationMsg('');

        if (!companyId) {
            setValidationMsg('Please select an account to proceed.');
            window.scrollTo(0, 0);
            return;
        }

        if (!showFor) {
            setValidationMsg('Please select Show For to proceed.');
            window.scrollTo(0, 0);
            return;
        }

        if (modulesLoading || modulesError || modulesList.length === 0) {
            setValidationMsg('Access modules are not available. Reload the modules and try again.');
            window.scrollTo(0, 0);
            return;
        }

        const finalRoleDefinition = Object.fromEntries(
            Object.entries(grid).filter(([, value]) => (
                value !== '' && value !== undefined && value !== null
            )),
        );
        const roleIdentity = buildRoleIdentity(
            companyId,
            roleName,
            editFlag ? editingRoleId : '',
        );

        setSaving(true);

        try {
            const response = await fetch(`${baseURL}/save_roles_setup`, {
                method: 'POST',
                headers: { 'Content-type': 'application/json' },
                body: JSON.stringify({
                    companyNumber: String(companyId),
                    account_name: String(companyName),
                    account_id: String(companyId),
                    ...roleIdentity,
                    show_for: showFor,
                    all_modules_access: allModulesAccess,
                    account_scope: accountScope,
                    role_definition: finalRoleDefinition,
                    flag: editFlag,
                }),
            });
            const data = await parseJsonResponse(response, 'Unable to save the role');

            if (data === 'Duplicate Entry') {
                setValidationMsg('Role Name already exists. Please try again with a different Role Name.');
                window.scrollTo(0, 0);
                return;
            }

            if (!Array.isArray(data)) {
                throw new Error('The saved roles response was not in the expected format');
            }

            setSuccessMsg('Role added/edited successfully.');
            setListOfRoles(data);
            resetRoleEditor(false);
            window.scrollTo(0, 0);
        } catch (error) {
            setValidationMsg(error.message || 'Unable to save the role. Please try again.');
            window.scrollTo(0, 0);
        } finally {
            setSaving(false);
        }
    };

    const editRole = (index) => {
        Swal.fire({
            title: 'Edit the selected Role?',
            showCancelButton: true,
            confirmButtonText: 'Yes',
        }).then((result) => {
            if (!result.isConfirmed) return;

            const selectedRole = listOfRoles[index];
            if (!selectedRole) {
                setValidationMsg('The selected role is no longer available. Reload the roles and try again.');
                return;
            }

            const savedRoleDefinition = (
                selectedRole.role_definition
                && typeof selectedRole.role_definition === 'object'
                && !Array.isArray(selectedRole.role_definition)
            ) ? selectedRole.role_definition : {};

            setEditFlag(true);
            setEditingRoleId(
                selectedRole._id
                || selectedRole.role_id
                || `${companyId}_${selectedRole.role_name || ''}`
            );
            setCreateNewFlag(true);
            setRoleName(selectedRole.role_name || '');
            setValue('roleName', selectedRole.role_name || '');
            setShowFor(selectedRole.show_for || DEFAULT_SHOW_FOR);
            setAllModulesAccess(selectedRole.all_modules_access === true);
            setAccountScope(selectedRole.account_scope || 'Permission based');
            setGrid({
                ...buildInitialGrid(modulesList),
                ...savedRoleDefinition,
            });
            setValidationMsg('');
            setSuccessMsg('');
        });
    };

    const canEditPermissions = !modulesLoading && !modulesError && modulesList.length > 0;

    return (
        <div className="roles-setup-form">
            <div className="row pt-2">
                <div className="col-12 px-3">
                    <span className="roles-setup-intro">Select an account to manage role-based access:</span>
                </div>
            </div>

            <div className="row pt-4 px-2">
                <div className="col-12 col-xl-10">
                    {validationMsg && <Alert severity="error">{validationMsg}</Alert>}
                    {successMsg && (
                        <Alert
                            severity="success"
                            className="mt-2"
                            onClose={() => setSuccessMsg('')}
                        >
                            {successMsg}
                        </Alert>
                    )}
                    {companiesError && (
                        <Alert
                            severity="error"
                            className="mt-2"
                            action={<Button color="inherit" size="small" onClick={loadCompanies}>Retry</Button>}
                        >
                            {companiesError}
                        </Alert>
                    )}
                    {modulesError && (
                        <Alert
                            severity="error"
                            className="mt-2"
                            action={<Button color="inherit" size="small" onClick={loadModules}>Retry</Button>}
                        >
                            {modulesError}
                        </Alert>
                    )}
                    {!companiesLoading && !companiesError && companyDetail.length === 0 && (
                        <Alert severity="info" className="mt-2">
                            No corporate accounts are available for role setup.
                        </Alert>
                    )}
                    {!modulesLoading && !modulesError && modulesList.length === 0 && (
                        <Alert severity="info" className="mt-2">
                            No access modules are available for configuration.
                        </Alert>
                    )}

                    <div className="row pt-3 align-items-center roles-setup-account-row">
                        <div className="col-12 col-sm-4 col-lg-2 mb-2">
                            <label htmlFor="company_name" className="form-label mb-0">Account Name</label>
                        </div>
                        <div className="col-12 col-sm-8 col-lg-5 mb-3">
                            <Select
                                {...ROLE_SELECT_PROPS}
                                value={selectedCompany}
                                inputId="company_name"
                                name="company_name"
                                options={companyNameOptions}
                                placeholder="Select Account Name"
                                onChange={handleInputChangeCompanyName}
                                isLoading={companiesLoading}
                                isDisabled={companiesLoading || Boolean(companiesError) || accountSelectorDisabled}
                                isClearable={!accountSelectorDisabled}
                                noOptionsMessage={() => (
                                    companiesLoading ? 'Loading accounts...' : 'No accounts available'
                                )}
                            />
                        </div>
                        <div className="col-12 col-sm-4 col-lg-2 mb-2">
                            <label htmlFor="company_id" className="form-label mb-0">Account ID</label>
                        </div>
                        <div className="col-12 col-sm-8 col-lg-3 mb-3">
                            <input
                                type="text"
                                className="form-control roles-setup-account-id"
                                value={companyId}
                                id="company_id"
                                placeholder="Selected automatically"
                                title={companyId}
                                disabled
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="row pt-3 px-2">
                <div className="col-12 col-xl-10">
                    {createNewFlag ? (
                        <Button variant="contained" onClick={() => setCreateNewFlag(false)}>- Hide</Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={!companyId || !canEditPermissions}
                            onClick={openCreateForm}
                        >
                            + Create New
                        </Button>
                    )}
                </div>
            </div>

            {companyId && modulesLoading && (
                <div className="roles-setup-state" role="status">
                    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                    Loading access modules...
                </div>
            )}

            {createNewFlag && canEditPermissions && (
                <form className="roles-setup-editor" onSubmit={handleSubmit(submitHandler)}>
                    <div className="row pt-4 px-2">
                        <div className="col-12 col-xl-10">
                            <div className="row align-items-start">
                                <div className="col-12 col-sm-4 col-lg-2 mb-2">
                                    <label htmlFor="roleName" className="form-label">Role Name *</label>
                                </div>
                                <div className="col-12 col-sm-8 col-lg-5 mb-3">
                                    <input
                                        type="text"
                                        disabled={editFlag}
                                        className={`form-control ${errors.roleName ? 'is-invalid' : ''}`}
                                        {...register('roleName', {
                                            onChange: (event) => setRoleName(event.target.value),
                                        })}
                                        id="roleName"
                                        value={roleName}
                                    />
                                    <div className="invalid-feedback">{errors.roleName?.message}</div>
                                </div>
                                <div className="col-12 col-sm-4 col-lg-2 mb-2">
                                    <label htmlFor="showFor" className="form-label">Show For</label>
                                </div>
                                <div className="col-12 col-sm-8 col-lg-3 mb-3">
                                    <Select
                                        {...ROLE_SELECT_PROPS}
                                        value={{ value: showFor, label: showFor }}
                                        inputId="showFor"
                                        name="showFor"
                                        options={showForOptions}
                                        onChange={(option) => setShowFor(option?.value || '')}
                                        isDisabled={editFlag}
                                    />
                                </div>
                                <div className="col-12 col-sm-4 col-lg-2 mb-2">
                                    <label htmlFor="accountScope" className="form-label">Account scope</label>
                                </div>
                                <div className="col-12 col-sm-8 col-lg-5 mb-3">
                                    <Select
                                        {...ROLE_SELECT_PROPS}
                                        value={{ value: accountScope, label: accountScope }}
                                        inputId="accountScope"
                                        name="accountScope"
                                        options={ACCOUNT_SCOPE_OPTIONS.map((value) => ({ value, label: value }))}
                                        onChange={(option) => setAccountScope(option?.value || 'Permission based')}
                                    />
                                    <small className="text-muted">
                                        Selected accounts are allocated on the User Setup screen. All accounts should be reserved for trusted administrators.
                                    </small>
                                </div>
                                <div className="col-12 col-lg-5 mb-3 d-flex align-items-center roles-setup-all-modules">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="allModulesAccess"
                                            checked={allModulesAccess}
                                            onChange={(event) => setAllModulesAccess(event.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="allModulesAccess">
                                            Grant access to all modules
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {allModulesAccess && (
                        <Alert severity="warning" className="mx-2 mb-3">
                            This role can open every module. The permission grid remains saved for compatibility but is not used to filter navigation.
                        </Alert>
                    )}

                    <div className="roles-setup-module-tools">
                        <div className="roles-setup-module-search">
                            <label htmlFor="module-search" className="form-label">Find a module</label>
                            <input
                                id="module-search"
                                type="search"
                                className="form-control"
                                value={moduleSearch}
                                placeholder="Search by module name"
                                onChange={(event) => setModuleSearch(event.target.value)}
                            />
                        </div>
                        <div className="roles-setup-module-filter">
                            <label htmlFor="module-filter" className="form-label">Permission type</label>
                            <select
                                id="module-filter"
                                className="form-select"
                                value={moduleFilter}
                                onChange={(event) => setModuleFilter(event.target.value)}
                            >
                                {MODULE_FILTER_OPTIONS.map((option) => (
                                    <option value={option.value} key={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="roles-setup-module-count" aria-live="polite">
                            Showing {filteredModules.length} of {modulesList.length} modules
                        </div>
                    </div>

                    <p className="roles-setup-grid-help">
                        Use the filters to focus the grid. A dash means that permission does not apply to that module.
                    </p>

                    <div className="roles-setup-table-scroll" tabIndex="0" aria-label="Role module permissions">
                        <table className="table table-bordered table-condensed text-center roles-setup-permissions-table">
                            <thead>
                                <tr>
                                    <th scope="col">Module</th>
                                    {COLUMN_LIST.map((columnName) => (
                                        <th scope="col" key={columnName}>{columnName}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="text-start">
                                {filteredModules.length === 0 && (
                                    <tr>
                                        <td colSpan={COLUMN_LIST.length + 1} className="roles-setup-no-results">
                                            No modules match the current search and filter.
                                        </td>
                                    </tr>
                                )}
                                {filteredModules.map((module, moduleIndex) => {
                                    const applicableColumns = getApplicableColumnIndexes(module.name);
                                    const moduleKey = module.id ?? module._id ?? `${module.name}-${moduleIndex}`;

                                    return (
                                        <tr key={moduleKey}>
                                            <th scope="row">{module.name}</th>
                                            {COLUMN_LIST.map((columnName, columnIndex) => {
                                                if (!applicableColumns.has(columnIndex)) {
                                                    return (
                                                        <td className="roles-setup-not-applicable" key={columnName}>
                                                            <span aria-label="Not applicable">-</span>
                                                        </td>
                                                    );
                                                }

                                                const key = getGridKey(module.name, columnName);
                                                const currentValue = grid[key] || '';
                                                const options = getOptionsWithCurrentValue(
                                                    getColumnOptions(columnIndex),
                                                    currentValue,
                                                );

                                                return (
                                                    <td key={columnName}>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={currentValue}
                                                            aria-label={`${module.name} ${columnName}`}
                                                            onChange={(event) => handleInputChangeGrid(
                                                                event.target.value,
                                                                module.name,
                                                                columnName,
                                                            )}
                                                        >
                                                            {options.map((option) => (
                                                                <option value={option} key={option}>{option}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="roles-setup-actions">
                        <button type="submit" className="btn btn-success" disabled={saving}>
                            {saving && (
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                />
                            )}
                            {saving ? 'Saving...' : 'Submit'}
                        </button>
                    </div>
                </form>
            )}

            {companyId && (
                <section className="roles-setup-existing" aria-labelledby="existing-roles-heading">
                    <h5 id="existing-roles-heading"><strong>Existing Roles</strong></h5>

                    {rolesLoading && (
                        <div className="roles-setup-state" role="status">
                            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                            Loading existing roles...
                        </div>
                    )}

                    {!rolesLoading && rolesError && (
                        <Alert
                            severity="error"
                            action={(
                                <Button color="inherit" size="small" onClick={() => loadRoles(companyId)}>
                                    Retry
                                </Button>
                            )}
                        >
                            {rolesError}
                        </Alert>
                    )}

                    {!rolesLoading && !rolesError && listOfRoles.length === 0 && (
                        <div className="roles-setup-state">No roles have been created for this account.</div>
                    )}

                    {!rolesLoading && !rolesError && listOfRoles.length > 0 && (
                        <div className="roles-setup-table-scroll">
                            <table className="table table-bordered table-condensed text-center roles-setup-existing-table">
                                <thead>
                                    <tr>
                                        <th scope="col">Sr. No.</th>
                                        <th scope="col">Account Name</th>
                                        <th scope="col">Role Name</th>
                                        <th scope="col">Show For</th>
                                        <th scope="col">Edit</th>
                                    </tr>
                                </thead>
                                <tbody className="text-start">
                                    {listOfRoles.map((role, index) => (
                                        <tr key={role._id || role.role_id || `${role.role_name}-${index}`}>
                                            <td>{index + 1}</td>
                                            <td>{role.account_name || companyName}</td>
                                            <td>{role.role_name || '-'}</td>
                                            <td>{role.show_for || '-'}</td>
                                            <td className="text-center">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => editRole(index)}
                                                    disabled={!canEditPermissions}
                                                    aria-label={`Edit ${role.role_name || 'role'}`}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}
